import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Box, BodyBig, BodySmall, TextField, Button, QrCodeIcon, Snackbar } from '@parspec/pixel';

import { ErrorStateType } from '../utils/types';
import { useGetUserInfoMutation, useHubspotTokenRequestMutation, useTwoFactorAuthLoginViaAuthenticatorMutation } from '../../queries';
import { AccountLockedPopup } from './AccountLockedPopup';
import { setUser } from '../../shared/ts-scripts/intercom/set_user';
import { setToken } from '../../shared/ts-scripts/hubspot/set_token';
import { REQUIRED_FIELD, someThingWentWrongMsg } from 'src/features/shared/constants';
import { useSnackbar } from 'src/features/shared/hooks/useSnackbar';

export const MFAAuthenticatorSignin = () => {
    const [authenticationCode, setAuthenticationCode] = useState<string>('');
    const [showAccountLockedPopup, setShowAccountLockedPopup] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<ErrorStateType>({
        otp: {
            isError: false,
            title: ''
        }
    });
    const { snackbarInfo, setSnackbarOpen, setSnackbarClose } = useSnackbar();

    const { mutateAsync: loginViaAuthenticatorMutation, isLoading: isLoginViaAuthenticatorMutationLoading } = useTwoFactorAuthLoginViaAuthenticatorMutation();
    const { mutateAsync: getUserProfileMutation, isLoading: isUserProfileLoading } = useGetUserInfoMutation();
    const { mutateAsync: hubspotTokenRequestMutation, isLoading: isHubspotTokenRequestMutationLoading } = useHubspotTokenRequestMutation();

    const twoFactorSettingsFromLs = localStorage.getItem('two_factor_settings') ? JSON.parse(localStorage.getItem('two_factor_settings') as string) : '';
    const nonceFromLs = localStorage.getItem('nonce') ?? '';

    const formRef = useRef<HTMLFormElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!twoFactorSettingsFromLs || !twoFactorSettingsFromLs?.is_authenticator_app_enabled) {
            navigate('/login');
            return;
        }
    }, []);

    const identifyUserForHubspotAndIntercom = async () => {
        const resp = await getUserProfileMutation();

        // getting parspec user email and name info
        const email = resp?.data.email as string;
        const user_id = resp?.data.id;
        let firstName = resp?.data.first_name;
        let lastName = resp?.data.last_name;

        if (!firstName) {
            const arr = resp?.data.name.trim().split(' ');
            firstName = arr ? arr[0].trim() : '';
        }
        if (!lastName) {
            const arr = resp?.data.name.trim().split(' ');
            if (arr && arr.length > 1) {
                lastName = arr[1];
            } else {
                lastName = '';
            }
        }

        // setting intercom
        setUser(email, user_id);

        const payload = { email, firstName, lastName };

        // get hubspot token
        const tokenRes = await hubspotTokenRequestMutation(payload);

        if (tokenRes?.data.token) {
            localStorage.setItem('hubspotToken', tokenRes.data.token);
            localStorage.setItem('hubspotEmail', email);
            setToken();
        }
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name: fieldName, value: fieldValue } = event.target;

        if (!Object.prototype.hasOwnProperty.call(errorState, fieldName)) {
            return;
        }

        if (errorState[fieldName].isError) {
            setErrorState({
                ...errorState,
                [fieldName]: { ...errorState[fieldName], isError: false, title: '' }
            });
        }
        if (!fieldValue.trim()) {
            setErrorState({
                ...errorState,
                [fieldName]: { ...errorState[fieldName], isError: true, title: REQUIRED_FIELD }
            });
        }

        setAuthenticationCode(() => fieldValue);
    };

    const keydownHandler = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (formRef.current) {
                formRef.current.requestSubmit();
            }
        }
    };

    const onSubmitHandler = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>) => {
        event.preventDefault();

        const isInputFieldEmpty = authenticationCode === '';

        if (isInputFieldEmpty) {
            return setErrorState({
                ...errorState,
                otp: { ...errorState.otp, isError: isInputFieldEmpty, title: `${isInputFieldEmpty ? 'This field is required.' : ''}` }
            });
        }

        const payload = {
            code: authenticationCode,
            nonce: nonceFromLs
        };

        try {
            const response = await loginViaAuthenticatorMutation(payload);
            if (response.status === 200) {
                localStorage.setItem('auth', response?.data?.auth);
                localStorage.setItem('token', response?.data?.token);
                identifyUserForHubspotAndIntercom();
                navigate('/v2/dashboard');
            }
        } catch (err: any) {
            if (err?.response?.status === 400) {
                localStorage.removeItem('nonce');
                localStorage.setItem('nonce', err?.response?.data?.nonce);
                return setErrorState({
                    ...errorState,
                    otp: { ...errorState.otp, isError: true, title: 'Invalid code, please try again.' }
                });
            } else if (err?.response?.status === 423) {
                setShowAccountLockedPopup(() => true);
            } else if (err?.response?.status === 404) {
                return setErrorState({
                    ...errorState,
                    otp: { ...errorState.otp, isError: true, title: 'Invalid code, please try again.' }
                });
            } else {
                setSnackbarOpen(someThingWentWrongMsg)
            }
        }
    };

    const redirectToRecoveryCodeScreen = async () => {
        navigate('/mfa/recovery-code');
    };

    const loadFlag = isLoginViaAuthenticatorMutationLoading || isHubspotTokenRequestMutationLoading || isUserProfileLoading;

    return (
        <>
            <Box>
                <Box bgcolor="secondary.contrastText" width="350px" padding={6} borderRadius={2}>
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                        <Box>
                            <QrCodeIcon fontSize="large" />
                        </Box>
                        <BodyBig limit={false} fontWeight={500} mt={1}>
                            Multi-Factor Authentication
                        </BodyBig>
                    </Box>

                    <Box my={4}>
                        <BodySmall limit={false} color="text.secondary">
                            Open your multi-factor authenticator app to view the authentication code.
                        </BodySmall>
                    </Box>

                    <Box>
                        <form onSubmit={onSubmitHandler} autoComplete={'off'} ref={formRef}>
                            <Box mb={4}>
                                <TextField
                                    required
                                    id={'otp'}
                                    name={'otp'}
                                    autoComplete={'otp'}
                                    label={'Authentication Code'}
                                    value={authenticationCode}
                                    error={errorState.otp.isError}
                                    helperText={errorState.otp.title}
                                    onKeyDown={keydownHandler}
                                    onChange={fieldChangeHandler}
                                />
                            </Box>
                        </form>
                    </Box>

                    <Box width="100%">
                        <Button onClick={onSubmitHandler} isLoading={loadFlag} fullWidth={true}>
                            Verify
                        </Button>
                    </Box>
                </Box>

                <Box bgcolor="secondary.contrastText" width="350px" padding={6} mt={4} borderRadius={2}>
                    <BodySmall limit={false} color="text.primary" fontWeight={500}>
                        Having problems?
                    </BodySmall>
                    <Box>
                        <ul style={{ padding: 6 }}>
                            <li style={{ marginLeft: 12 }}>
                                <BodySmall limit={false} color={'primary.main'} sx={{ cursor: 'pointer' }} onClick={redirectToRecoveryCodeScreen}>
                                    Sign in via recovery code.
                                </BodySmall>
                            </li>
                            <li style={{ marginLeft: 12 }}>
                                <BodySmall limit={false}>Contact our team in the Support Chat</BodySmall>
                                <BodySmall limit={false}>
                                    or at <span style={{ color: '#6467F2' }}>support@parspec.io.</span>
                                </BodySmall>
                            </li>
                        </ul>
                    </Box>
                </Box>
            </Box>

            {
                <Snackbar
                    open={snackbarInfo.open}
                    message={snackbarInfo.message}
                    onClose={() => setSnackbarClose()}
                />
            }

            <AccountLockedPopup isOpen={showAccountLockedPopup} setIsOpen={setShowAccountLockedPopup} />
        </>
    );
};
